import { Archive } from "lucide-react";
import type {
  SearchAttribution,
  SearchClaim,
  SearchEdge,
  SearchNode,
  SearchSource,
} from "../api/types";
import { confidenceColor, formatPercent } from "../lib/format";

type Props = {
  node: SearchNode;
  edges: SearchEdge[];
  nodesById: Map<string, SearchNode>;
  claims: SearchClaim[];
  sources: SearchSource[];
  attribution: SearchAttribution[];
  onSelectNode: (id: string) => void;
  onMaterializeSource?: (source: SearchSource) => void;
  onClose: () => void;
};

const Section = ({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
}) => (
  <div className="border-t border-white/5 pt-4 mt-4 first:border-t-0 first:pt-0 first:mt-0">
    <div className="flex items-center gap-2 mb-3">
      <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
        {title}
      </h3>
      {count !== undefined && (
        <span className="font-mono text-[10px] text-white/30 tabular-nums">
          {count}
        </span>
      )}
    </div>
    {children}
  </div>
);

const ConfidencePill = ({ value }: { value: "low" | "medium" | "high" }) => (
  <span
    className="chip"
    style={{
      color: confidenceColor(value),
      borderColor: `${confidenceColor(value)}33`,
      background: `${confidenceColor(value)}10`,
    }}
  >
    {value}
  </span>
);

const shortWallet = (w: string) =>
  w.length <= 10 ? w : `${w.slice(0, 6)}…${w.slice(-4)}`;

const isHttpUrl = (url: string | null): url is string =>
  Boolean(url && /^https?:\/\//i.test(url));

export const NodePanel = ({
  node,
  edges,
  nodesById,
  claims,
  sources,
  attribution,
  onSelectNode,
  onMaterializeSource,
  onClose,
}: Props) => {
  const sourcesById = new Map(sources.map((s) => [s.id, s]));

  const incidentEdges = edges.filter(
    (e) => e.source_node_id === node.id || e.target_node_id === node.id,
  );
  const nodeClaims = claims.filter((c) => c.node_id === node.id);
  const nodeAttribution = attribution.filter(
    (a) => a.item_type === "node" && a.item_id === node.id,
  );

  return (
    <aside className="panel rounded-2xl shadow-terminal overflow-hidden flex flex-col max-h-full">
      <div className="p-5 pb-4 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="chip">{node.type}</span>
            <ConfidencePill value={node.confidence} />
            <span className="chip chip-muted">
              q {formatPercent(node.quality_score)}
            </span>
            <span className="chip chip-muted">depth {node.depth}</span>
          </div>
          <h2 className="font-display text-xl font-bold text-white tracking-tight leading-snug">
            {node.title}
          </h2>
          <div className="font-mono text-[11px] text-white/30 mt-1 truncate">
            {node.slug}
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-white/40 hover:text-white text-lg leading-none w-7 h-7 rounded-md hover:bg-white/5 flex items-center justify-center"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="px-5 pb-5 overflow-y-auto panel-scroll">
        {node.summary && (
          <p className="text-sm text-white/70 leading-relaxed">
            {node.summary}
          </p>
        )}

        {incidentEdges.length > 0 && (
          <Section title="Connections" count={incidentEdges.length}>
            <ul className="space-y-2">
              {incidentEdges.map((e) => {
                const otherId =
                  e.source_node_id === node.id
                    ? e.target_node_id
                    : e.source_node_id;
                const other = nodesById.get(otherId);
                const outgoing = e.source_node_id === node.id;
                return (
                  <li key={e.id}>
                    <button
                      onClick={() => other && onSelectNode(other.id)}
                      disabled={!other}
                      className="w-full text-left group flex items-start gap-2 p-2 -mx-2 rounded-md hover:bg-white/[0.03] transition disabled:cursor-default"
                    >
                      <span className="font-mono text-[10px] text-white/30 mt-0.5 select-none">
                        {outgoing ? "→" : "←"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-sm text-white/90 group-hover:text-flame-400 transition truncate">
                            {other?.title ?? "(outside this view)"}
                          </span>
                          <span className="font-mono text-[10px] text-white/40">
                            {e.edge_type}
                          </span>
                        </div>
                        {e.claim && (
                          <div className="text-xs text-white/50 mt-0.5 leading-snug">
                            {e.claim}
                          </div>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Section>
        )}

        {nodeClaims.length > 0 && (
          <Section title="Claims" count={nodeClaims.length}>
            <ul className="space-y-3">
              {nodeClaims.map((c) => {
                const src = c.source_id ? sourcesById.get(c.source_id) : null;
                return (
                  <li key={c.id} className="text-sm">
                    <div className="flex items-start gap-2">
                      <span
                        className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0"
                        style={{ background: confidenceColor(c.confidence) }}
                      />
                      <div className="flex-1">
                        <p className="text-white/85 leading-relaxed">
                          {c.text}
                        </p>
                        {src && (
                          <div className="mt-1 flex items-center gap-2 min-w-0">
                            <a
                              href={src.url ?? "#"}
                              target="_blank"
                              rel="noreferrer"
                              className="font-mono text-[11px] text-flame-400/70 hover:text-flame-400 truncate min-w-0"
                            >
                              {src.title ?? src.url ?? src.id}
                            </a>
                            {isHttpUrl(src.url) && onMaterializeSource && (
                              <button
                                onClick={() => onMaterializeSource(src)}
                                className="chip chip-muted flex-shrink-0 hover:opacity-80"
                              >
                                <Archive className="h-3 w-3" />
                                vault
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Section>
        )}

        {nodeAttribution.length > 0 && (
          <Section title="Attribution" count={nodeAttribution.length}>
            <ul className="space-y-1.5">
              {nodeAttribution.map((a, i) => (
                <li
                  key={`${a.item_id}-${a.contributor_id ?? i}`}
                  className="flex items-center justify-between font-mono text-[11px]"
                >
                  <span className="text-white/60">
                    {a.contributor_wallet
                      ? shortWallet(a.contributor_wallet)
                      : (a.contributor_id ?? "—")}
                  </span>
                  <span className="text-flame-400/80 tabular-nums">
                    {formatPercent(a.weight)}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </aside>
  );
};
